/**
 * Till arithmetic and parsing that has no business living inside a component. Pure, in
 * integer paise and milli-units like the rest of the till, and covered by till.test.ts.
 *
 *   - the UPI deep link behind the payment QR
 *   - how to hand change back in notes and coins
 *   - what the customer saved against MRP
 *   - "2 kg tomato", "₹50 onion", a mobile number: what the cashier typed, understood
 *   - price to weight for loose goods
 */

import { amount } from './format';
import { tr } from '../i18n';

// ---------------------------------------------------------------- UPI

export interface UpiPayment {
  /** The store's UPI ID (virtual payment address), e.g. `sahyadri@okhdfcbank`. */
  vpa: string;
  payeeName: string;
  amountPaise: number;
  /** Shown to the customer in their UPI app. */
  note?: string;
}

/**
 * The NPCI `upi://pay` link. The amount travels in the link, so the customer's app shows it
 * filled in and nobody types it: the commonest UPI mistake at a counter is a mistyped amount.
 */
export function upiPayUri(payment: UpiPayment): string {
  const params: [string, string][] = [
    ['pa', payment.vpa.trim()],
    ['pn', payment.payeeName.trim()],
    ['am', amount(payment.amountPaise)],
    ['cu', 'INR'],
  ];
  if (payment.note?.trim()) params.push(['tn', payment.note.trim()]);
  return 'upi://pay?' + params.map(([key, value]) => `${key}=${encodeURIComponent(value)}`).join('&');
}

// ---------------------------------------------------------------- change

/** Notes a drawer actually holds, largest first. ₹2000 has been withdrawn from circulation. */
export const NOTE_RUPEES = [500, 200, 100, 50, 20, 10] as const;
const COIN_RUPEES = [5, 2, 1] as const;

export interface ChangePiece {
  /** Face value in paise. */
  paise: number;
  count: number;
  kind: 'note' | 'coin' | 'paise';
}

/**
 * Change as the notes and coins to hand back, greedily. A cashier counting ₹65 out of a
 * drawer under pressure gets it wrong far more often than one told "₹50 + ₹10 + ₹5".
 */
export function changeBreakdown(changePaise: number): ChangePiece[] {
  if (!Number.isInteger(changePaise) || changePaise <= 0) return [];
  const pieces: ChangePiece[] = [];
  let left = changePaise;
  for (const rupee of NOTE_RUPEES) left = take(left, rupee * 100, 'note', pieces);
  for (const rupee of COIN_RUPEES) left = take(left, rupee * 100, 'coin', pieces);
  if (left > 0) pieces.push({ paise: left, count: 1, kind: 'paise' });
  return pieces;
}

function take(left: number, face: number, kind: ChangePiece['kind'], into: ChangePiece[]): number {
  const count = Math.floor(left / face);
  if (count > 0) into.push({ paise: face, count, kind });
  return left - count * face;
}

/** `₹50 note`, `2 × ₹1 coin`, `50 paise`. */
export function describePiece(piece: ChangePiece): string {
  if (piece.kind === 'paise') return tr('change.paise', { value: piece.paise });
  const face = tr('change.face', { value: piece.paise / 100, kind: tr(piece.kind === 'note' ? 'change.note' : 'change.coin') });
  return piece.count > 1 ? tr('change.many', { count: piece.count, face }) : face;
}

// ---------------------------------------------------------------- savings

export interface SavingsLine {
  mrpPaise: number;
  ratePaise: number;
  qtyMilli: number;
}

/** What one line saves against its printed MRP. Selling above MRP saves nothing (and is flagged elsewhere). */
export function lineMrpSavingsPaise(line: SavingsLine): number {
  if (line.mrpPaise <= line.ratePaise || line.qtyMilli <= 0) return 0;
  return Math.round(((line.mrpPaise - line.ratePaise) * line.qtyMilli) / 1000);
}

export function mrpSavingsPaise(lines: SavingsLine[]): number {
  return lines.reduce((sum, line) => sum + lineMrpSavingsPaise(line), 0);
}

// ---------------------------------------------------------------- loose goods

/** How much of a loose product a rupee amount buys: "₹50 of tomatoes" at ₹40/kg is 1.250 kg. */
export function qtyForAmount(amountPaise: number, ratePaise: number): number {
  if (amountPaise <= 0 || ratePaise <= 0) return 0;
  return Math.round((amountPaise * 1000) / ratePaise);
}

/** Rate × quantity, for showing a line before the pricing engine has seen it. */
export function amountForQty(qtyMilli: number, ratePaise: number): number {
  return Math.round((qtyMilli * ratePaise) / 1000);
}

/** "1.25" typed in kg into milli-units. Junk and negatives are 0. */
export function parseQtyToMilli(text: string): number {
  const cleaned = text.replace(/[^\d.]/g, '');
  if (cleaned === '' || cleaned === '.') return 0;
  const [whole = '0', fraction = ''] = cleaned.split('.');
  return Number.parseInt(whole || '0', 10) * 1000 + Number.parseInt((fraction + '000').slice(0, 3), 10);
}

// ---------------------------------------------------------------- what the cashier typed

export type QuickQuery =
  | { kind: 'empty' }
  | { kind: 'phone'; phone: string }
  | { kind: 'barcode'; code: string }
  | {
      kind: 'item';
      /** What to search the catalogue for. */
      term: string;
      /** The number typed in front of the term, if any, with its unit. */
      quantity: { value: number; unit: QuantityUnit } | null;
      /** "₹50 onion": a rupee amount of a loose item. */
      amountPaise: number | null;
    };

export type QuantityUnit = 'kg' | 'g' | 'l' | 'ml' | 'count' | 'none';

const UNIT_WORDS: Record<string, QuantityUnit> = {
  kg: 'kg', kgs: 'kg', kilo: 'kg', kilos: 'kg',
  g: 'g', gm: 'g', gms: 'g', gram: 'g', grams: 'g',
  l: 'l', ltr: 'l', litre: 'l', liter: 'l', litres: 'l', liters: 'l',
  ml: 'ml',
  x: 'count', pc: 'count', pcs: 'count', nos: 'count', no: 'count', pkt: 'count', pkts: 'count',
};

/**
 * Understand the search box. Only a number IN FRONT of the words is a quantity: "Tata Salt 1kg"
 * is a product name, and reading its "1kg" as a quantity would search for "Tata Salt" and add
 * the wrong pack size.
 */
export function parseQuickQuery(raw: string): QuickQuery {
  const text = raw.trim().replace(/\s+/g, ' ');
  if (text === '') return { kind: 'empty' };

  const compact = text.replace(/[\s-]/g, '');
  if (/^(\+?91)?[6-9]\d{9}$/.test(compact)) return { kind: 'phone', phone: compact.slice(-10) };
  if (/^\d{6,14}$/.test(compact)) return { kind: 'barcode', code: compact };

  const money = /^(?:₹|rs\.?|inr)\s*(\d+(?:\.\d{1,2})?)\s+(.+)$/i.exec(text);
  if (money) {
    return { kind: 'item', term: money[2]!.trim(), quantity: null, amountPaise: Math.round(Number(money[1]) * 100) };
  }

  const counted = /^(\d+(?:\.\d{1,3})?)\s*([a-z]+)?\s+(.+)$/i.exec(text);
  if (counted) {
    const word = counted[2]?.toLowerCase();
    const unit = word === undefined ? 'none' : UNIT_WORDS[word];
    // A word after the number that is not a unit ("3 amul butter") belongs to the term, so the
    // pattern is only trusted when that word is a unit or absent. The till shows the parsed
    // quantity on the best match before anything is added, so "7 up" cannot slip through unseen.
    if (unit !== undefined) {
      return { kind: 'item', term: counted[3]!.trim(), quantity: { value: Number(counted[1]), unit }, amountPaise: null };
    }
  }

  return { kind: 'item', term: text, quantity: null, amountPaise: null };
}

export type ResolvedQuantity = { qtyMilli: number; error?: never } | { qtyMilli?: never; error: string };

/**
 * Turn what was typed into a quantity for one product, or say why it does not fit. Counted
 * goods take whole numbers; loose goods take weights (a bare number is kilograms) or rupees.
 */
export function resolveQuantity(
  query: Extract<QuickQuery, { kind: 'item' }>,
  product: { allowDecimalQty: boolean; sellingPricePaise: number },
): ResolvedQuantity {
  const loose = product.allowDecimalQty;
  if (query.amountPaise !== null) {
    if (!loose) return { error: 'A rupee amount works for loose items only. Type a count instead.' };
    const qtyMilli = qtyForAmount(query.amountPaise, product.sellingPricePaise);
    return qtyMilli > 0 ? { qtyMilli } : { error: 'That amount is too small for this item.' };
  }
  if (!query.quantity) return { qtyMilli: loose ? 0 : 1000 };

  const { value, unit } = query.quantity;
  if (!(value > 0)) return { error: 'Enter a quantity above zero.' };
  const weight = unit === 'kg' || unit === 'g' || unit === 'l' || unit === 'ml';
  if (!loose && weight) return { error: 'This item is sold by count, not by weight.' };
  if (!loose && !Number.isInteger(value)) return { error: 'This item is sold by count. Use a whole number.' };

  const milli = unit === 'g' || unit === 'ml' ? Math.round(value) : Math.round(value * 1000);
  return milli > 0 ? { qtyMilli: milli } : { error: 'Enter a quantity above zero.' };
}

// ---------------------------------------------------------------- presentation helpers

/** A product's two-letter mark: "Tomato (Loose)" is TO, "Amul Butter 500g" is AB. */
export function monogram(name: string): string {
  // Words that start with a letter: pack sizes such as "600ml" and "(Loose)" say nothing about the product.
  const words = name
    .replace(/\(.*?\)/g, ' ')
    .split(/\s+/)
    .filter((word) => /^[A-Za-z]/.test(word));
  if (words.length === 0) return name.slice(0, 2).toUpperCase() || '?';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[1]![0]!).toUpperCase();
}

/** One of eight category tones, stable for a category name so an aisle keeps its colour everywhere. */
export function toneOf(category: string | null | undefined): number {
  const key = (category ?? '').split('/')[0]!.trim().toLowerCase();
  let hash = 0;
  for (let index = 0; index < key.length; index += 1) hash = (hash * 31 + key.charCodeAt(index)) >>> 0;
  return key === '' ? 7 : hash % 8;
}

/** "Expires today", "Expires in 2 days", "Expired 3 days ago" — or null when it is not close. */
export function expiryNote(daysLeft: number | null, warnWithinDays = 3): { text: string; tone: 'warn' | 'danger' } | null {
  if (daysLeft === null || daysLeft > warnWithinDays) return null;
  if (daysLeft < 0) return { text: tr('expiry.expired', { count: -daysLeft }), tone: 'danger' };
  if (daysLeft === 0) return { text: tr('expiry.today'), tone: 'danger' };
  return { text: tr('expiry.in', { count: daysLeft }), tone: 'warn' };
}

/** "today", "yesterday", "3 days ago", "5 Oct". For a returning customer's last visit. */
export function lastVisitLabel(iso: string | null | undefined, now = new Date()): string | null {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return null;
  const start = (date: Date) => Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  const days = Math.round((start(now) - start(then)) / 86_400_000);
  if (days <= 0) return tr('visit.today');
  if (days === 1) return tr('visit.yesterday');
  if (days < 30) return tr('visit.daysAgo', { count: days });
  return new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' }).format(then);
}

/** The digits to put after wa.me/: an Indian mobile with its country code, or null if it is not one. */
export function whatsappNumber(phone: string | null | undefined): string | null {
  const digits = (phone ?? '').replace(/\D/g, '');
  if (/^[6-9]\d{9}$/.test(digits)) return '91' + digits;
  if (/^91[6-9]\d{9}$/.test(digits)) return digits;
  return null;
}

// ---------------------------------------------------------------- the till's view

/** Which pane the phone-sized till opens on: the store's choice, the Bill check view when nothing is set. */
export function initialTillView(config: { mobilePrimaryView?: 'items' | 'bill' } | null | undefined): 'items' | 'bill' {
  return config?.mobilePrimaryView === 'items' ? 'items' : 'bill';
}
